/**
 * Pure transforms from Claude Code transcript JSONL records into
 * harness-neutral `HarnessEvent`s.  This is the parity core: the events it
 * produces must match what `ClaudeCodeHarness` yields for the same logical
 * messages (see `helpers/mock-sdk.ts`).
 *
 * Transcript files are a trusted on-disk artifact written by the `claude`
 * CLI, so parsing their lines is allowed — unlike raw PTY stdout, which is
 * never parsed.
 */

import type {
  HarnessEvent,
  HarnessAssistantEvent,
  HarnessContentBlock,
  HarnessResultEvent,
  HarnessSystemInitEvent,
} from "../types.js";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** Custom predicate so narrowing yields `unknown[]`, not `any[]`. */
function isUnknownArray(value: unknown): value is unknown[] {
  return Array.isArray(value);
}

function stringField(record: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string") return value;
  }
  return undefined;
}

function numberField(record: Record<string, unknown>, ...keys: string[]): number | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "number") return value;
  }
  return undefined;
}

function mapSystem(record: Record<string, unknown>): HarnessEvent | null {
  if (record.subtype === "init") {
    const event: HarnessSystemInitEvent = {
      type: "system",
      subtype: "init",
      model: stringField(record, "model") ?? "",
    };
    const sessionId = stringField(record, "session_id", "sessionId");
    if (sessionId !== undefined) event.session_id = sessionId;
    return event;
  }
  // End-of-turn: the interactive CLI never writes the headless
  // `{"type":"result"}` record — it marks a completed turn with a
  // `turn_duration` system record (verified against live 2.1.202 transcripts,
  // where result records appear in 0 of 95 sessions). Map it to the result
  // event the harness keys every turn boundary on (endTurn/parking, idle
  // status flips); without this, PTY turns never end.
  if (record.subtype === "turn_duration") {
    const event: HarnessResultEvent = {
      type: "result",
      subtype: "success",
      result: "",
      total_cost_usd: 0,
    };
    const sessionId = stringField(record, "session_id", "sessionId");
    if (sessionId !== undefined) event.sessionId = sessionId;
    return event;
  }
  return null;
}

type AssistantUsage = NonNullable<HarnessAssistantEvent["message"]["usage"]>;

function mapUsage(message: Record<string, unknown>): AssistantUsage | undefined {
  const usage = message.usage;
  if (!isRecord(usage)) return undefined;
  const result: AssistantUsage = {};
  const input = numberField(usage, "input_tokens");
  const cacheRead = numberField(usage, "cache_read_input_tokens");
  const cacheCreation = numberField(usage, "cache_creation_input_tokens");
  if (input !== undefined) result.input_tokens = input;
  if (cacheRead !== undefined) result.cache_read_input_tokens = cacheRead;
  if (cacheCreation !== undefined) result.cache_creation_input_tokens = cacheCreation;
  return result;
}

function mapContentBlock(raw: unknown): HarnessContentBlock | null {
  if (!isRecord(raw)) return null;
  const type = stringField(raw, "type");
  if (type === undefined) return null;
  const block: HarnessContentBlock = { type };
  const text = stringField(raw, "text");
  if (text !== undefined) block.text = text;
  const name = stringField(raw, "name");
  if (name !== undefined) block.name = name;
  const id = stringField(raw, "id");
  if (id !== undefined) block.id = id;
  if ("input" in raw) block.input = raw.input;
  return block;
}

function mapAssistant(record: Record<string, unknown>): HarnessAssistantEvent | null {
  const message = record.message;
  if (!isRecord(message)) return null;
  const rawContent: unknown[] = isUnknownArray(message.content) ? message.content : [];
  const content: HarnessContentBlock[] = [];
  for (const item of rawContent) {
    const block = mapContentBlock(item);
    if (block) content.push(block);
  }
  const result: HarnessAssistantEvent = {
    type: "assistant",
    message: { role: "assistant", content },
  };
  const usage = mapUsage(message);
  if (usage !== undefined) result.message.usage = usage;
  return result;
}

function mapResultSuccess(record: Record<string, unknown>): HarnessResultEvent {
  const event: HarnessResultEvent = {
    type: "result",
    subtype: "success",
    result: stringField(record, "result") ?? "",
    total_cost_usd: numberField(record, "total_cost_usd", "totalCostUsd") ?? 0,
  };
  const modelUsage = record.modelUsage;
  if (isRecord(modelUsage)) event.modelUsage = modelUsage;
  const sessionId = stringField(record, "session_id", "sessionId");
  if (sessionId !== undefined) event.sessionId = sessionId;
  return event;
}

function mapResultError(record: Record<string, unknown>): HarnessResultEvent {
  const rawErrors = isUnknownArray(record.errors) ? record.errors : [];
  const errors = rawErrors.filter((e): e is string => typeof e === "string");
  const event: HarnessResultEvent = { type: "result", subtype: "error", errors };
  const sessionId = stringField(record, "session_id", "sessionId");
  if (sessionId !== undefined) event.sessionId = sessionId;
  return event;
}

function mapResult(record: Record<string, unknown>): HarnessResultEvent | null {
  if (record.subtype === "success") return mapResultSuccess(record);
  if (record.subtype === "error") return mapResultError(record);
  return null;
}

export function mapTranscriptRecord(raw: unknown): HarnessEvent | null {
  if (!isRecord(raw)) return null;
  switch (raw.type) {
    case "system":
      return mapSystem(raw);
    case "assistant":
      return mapAssistant(raw);
    case "result":
      return mapResult(raw);
    default:
      return null;
  }
}
